import React from 'react';

/**
 * EmptyState - Illustrated empty state component
 *
 * @param {string} icon - Emoji or icon to display (fallback if no illustration)
 * @param {string} illustration - Type of illustration ('customers', 'properties', 'loans', 'payments', 'team', 'data', 'search')
 * @param {string} title - Title text
 * @param {string} description - Description text
 * @param {ReactNode} action - Optional action button/element
 */
function EmptyState({
  icon,
  illustration,
  title = 'No data yet',
  description = 'Get started by adding your first item.',
  action
}) {
  return (
    <div className="empty-state">
      {illustration ? (
        <div className="empty-state-illustration">
          <EmptyIllustration type={illustration} />
        </div>
      ) : icon ? (
        <div className="empty-state-icon">{icon}</div>
      ) : null}
      <h3 className="empty-state-title">{title}</h3>
      <p className="empty-state-description">{description}</p>
      {action && <div className="empty-state-action">{action}</div>}
    </div>
  );
}

// SVG Illustrations
function EmptyIllustration({ type }) {
  const illustrations = {
    customers: (
      <svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="60" r="30" fill="#e0e7ff" stroke="#6366f1" strokeWidth="2"/>
        <circle cx="100" cy="50" r="12" fill="#6366f1"/>
        <path d="M75 85 C75 70, 125 70, 125 85 L125 95 C125 100, 120 105, 100 105 C80 105, 75 100, 75 95 Z" fill="#6366f1" opacity="0.3"/>
        <circle cx="55" cy="70" r="20" fill="#f0f0f0" stroke="#d1d5db" strokeWidth="2"/>
        <circle cx="145" cy="70" r="20" fill="#f0f0f0" stroke="#d1d5db" strokeWidth="2"/>
        <text x="100" y="130" textAnchor="middle" fill="#9ca3af" fontSize="12">No customers yet</text>
      </svg>
    ),
    properties: (
      <svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="40" y="60" width="120" height="70" rx="4" fill="#e0e7ff" stroke="#6366f1" strokeWidth="2"/>
        <path d="M30 65 L100 25 L170 65" stroke="#6366f1" strokeWidth="3" fill="none" strokeLinecap="round"/>
        <rect x="60" y="90" width="30" height="40" fill="#6366f1" opacity="0.3"/>
        <rect x="110" y="80" width="30" height="25" fill="#6366f1" opacity="0.5"/>
        <circle cx="165" cy="40" r="15" fill="#fbbf24" opacity="0.5"/>
        <text x="100" y="145" textAnchor="middle" fill="#9ca3af" fontSize="12">No properties listed</text>
      </svg>
    ),
    loans: (
      <svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="50" y="40" width="100" height="70" rx="8" fill="#e0e7ff" stroke="#6366f1" strokeWidth="2"/>
        <text x="100" y="65" textAnchor="middle" fill="#6366f1" fontSize="24" fontWeight="bold">$</text>
        <line x1="70" y1="85" x2="130" y2="85" stroke="#6366f1" strokeWidth="2" strokeDasharray="4"/>
        <line x1="70" y1="95" x2="110" y2="95" stroke="#6366f1" strokeWidth="2" strokeDasharray="4" opacity="0.5"/>
        <circle cx="150" cy="35" r="12" fill="#10b981" opacity="0.3"/>
        <path d="M145 35 L148 38 L155 31" stroke="#10b981" strokeWidth="2" fill="none"/>
        <text x="100" y="130" textAnchor="middle" fill="#9ca3af" fontSize="12">No active loans</text>
      </svg>
    ),
    payments: (
      <svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="40" y="45" width="120" height="75" rx="6" fill="#e0e7ff" stroke="#6366f1" strokeWidth="2"/>
        <rect x="40" y="45" width="120" height="20" fill="#6366f1"/>
        <circle cx="55" cy="55" r="6" fill="#fbbf24"/>
        <rect x="55" y="80" width="50" height="8" rx="2" fill="#6366f1" opacity="0.3"/>
        <rect x="55" y="95" width="70" height="8" rx="2" fill="#6366f1" opacity="0.2"/>
        <text x="100" y="140" textAnchor="middle" fill="#9ca3af" fontSize="12">No payments recorded</text>
      </svg>
    ),
    team: (
      <svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="50" r="25" fill="#e0e7ff" stroke="#6366f1" strokeWidth="2"/>
        <circle cx="100" cy="42" r="10" fill="#6366f1"/>
        <path d="M80 70 Q80 55, 100 55 Q120 55, 120 70 L120 75 Q120 80, 100 80 Q80 80, 80 75 Z" fill="#6366f1" opacity="0.4"/>
        <circle cx="50" cy="65" r="18" fill="#f0f0f0" stroke="#d1d5db" strokeWidth="2"/>
        <circle cx="150" cy="65" r="18" fill="#f0f0f0" stroke="#d1d5db" strokeWidth="2"/>
        <path d="M85 90 L100 105 L115 90" stroke="#6366f1" strokeWidth="2" strokeDasharray="4" fill="none"/>
        <text x="100" y="130" textAnchor="middle" fill="#9ca3af" fontSize="12">Invite team members</text>
      </svg>
    ),
    data: (
      <svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="45" y="50" width="110" height="70" rx="4" fill="#f0f0f0" stroke="#d1d5db" strokeWidth="2"/>
        <line x1="60" y1="70" x2="140" y2="70" stroke="#d1d5db" strokeWidth="2"/>
        <line x1="60" y1="85" x2="120" y2="85" stroke="#e5e5e5" strokeWidth="2"/>
        <line x1="60" y1="100" x2="130" y2="100" stroke="#e5e5e5" strokeWidth="2"/>
        <circle cx="155" cy="45" r="20" fill="#e0e7ff" stroke="#6366f1" strokeWidth="2"/>
        <text x="155" y="50" textAnchor="middle" fill="#6366f1" fontSize="16">?</text>
        <text x="100" y="140" textAnchor="middle" fill="#9ca3af" fontSize="12">No data available</text>
      </svg>
    ),
    search: (
      <svg viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="90" cy="60" r="35" fill="#e0e7ff" stroke="#6366f1" strokeWidth="3"/>
        <circle cx="90" cy="60" r="20" fill="white" stroke="#6366f1" strokeWidth="2"/>
        <line x1="115" y1="85" x2="145" y2="115" stroke="#6366f1" strokeWidth="4" strokeLinecap="round"/>
        <text x="90" y="65" textAnchor="middle" fill="#6366f1" fontSize="14">?</text>
        <text x="100" y="140" textAnchor="middle" fill="#9ca3af" fontSize="12">No results found</text>
      </svg>
    )
  };

  return illustrations[type] || illustrations.data;
}

// Pre-built empty states for common scenarios
export function EmptyCustomers({ onAdd }) {
  return (
    <EmptyState
      illustration="customers"
      title="No customers yet"
      description="Customers will appear here when they sign up or when you create loans."
      action={onAdd && (
        <button className="btn btn-primary" onClick={onAdd}>
          Add First Customer
        </button>
      )}
    />
  );
}

export function EmptyProperties({ onAdd }) {
  return (
    <EmptyState
      illustration="properties"
      title="No properties listed"
      description="Add your first property to start selling land with financing options."
      action={onAdd && (
        <button className="btn btn-primary" onClick={onAdd}>
          Add Property
        </button>
      )}
    />
  );
}

export function EmptyLoans({ onAdd }) {
  return (
    <EmptyState
      illustration="loans"
      title="No active loans"
      description="Loans will appear here when customers purchase properties with financing."
      action={onAdd && (
        <button className="btn btn-primary" onClick={onAdd}>
          Create Loan
        </button>
      )}
    />
  );
}

export function EmptyPayments() {
  return (
    <EmptyState
      illustration="payments"
      title="No payments yet"
      description="Payment history will appear here once customers start making payments."
    />
  );
}

export function EmptyTeam({ onInvite }) {
  return (
    <EmptyState
      illustration="team"
      title="No team members yet"
      description="Invite your team to help manage properties, customers, and loans."
      action={onInvite && (
        <button className="btn btn-primary" onClick={onInvite}>
          Invite Team Member
        </button>
      )}
    />
  );
}

export function EmptySearchResults({ searchTerm }) {
  return (
    <EmptyState
      illustration="search"
      title="No results found"
      description={searchTerm ? `No matches for "${searchTerm}". Try a different search term.` : 'Try adjusting your search or filters.'}
    />
  );
}

export default EmptyState;
